import { POST } from '@webapp/account/src/libs/request';
import { formatDateForFilters, getFilterParams, getReportSsid } from '@webapp/account/src/resources/lib';
import type { GlobalFilter } from '@webapp/account/src/resources/reports/global-filters';
import { mapReportStat } from '@webapp/account/src/resources/reports/map-report';
import type { Report } from '@webapp/account/src/resources/reports/reports';

import type { ChartGrouping } from '@webapp/common/lib/date';
import { undefDestroy } from '@webapp/common/lib/utils';
import type { Question } from '@webapp/common/resources/entities/question';
import { QuestionType } from '@webapp/common/resources/survey';

const listOmitFilter = ['comments', 'responses'];

export interface ReportsStatPeriod {
    dateFrom: string;
    dateTo: string;
}

export const buildReportParams = (
    globalFilters: GlobalFilter,
    reports: Array<Report | Question>,
    question: Array<number>,
    page: number,
    limit: number,
    period?: ReportsStatPeriod,
): AnyObject => {
    const params = Object.assign({
        ...getFilterParams(globalFilters, reports),
        question: JSON.stringify(question),
        page,
        limit,
    });

    // getFilterParams берёт даты из getUriFilters() || globalFilters, поэтому период
    // сравнения перебиваем ПОСЛЕ вызова — иначе в PDF все периоды приедут одинаковыми.
    if (period) {
        params.start = formatDateForFilters(period.dateFrom);
        params.end = formatDateForFilters(period.dateTo);
    }

    return params;
};

export const fetchReportsStat = async ({
    surveyId,
    reports,
    allReportList,
    globalFilters,
    responsesLimit,
    page,
    field,
    group,
    period,
}: {
    surveyId: number;
    reports: Array<Report>;
    allReportList: Array<Report | Question>;
    globalFilters: GlobalFilter;
    responsesLimit: number;
    page?: number;
    field?: string;
    group?: ChartGrouping;
    period?: ReportsStatPeriod;
}): Promise<{ reports: Array<Report>; filesSize: number; filesCount: number }> => {
    // В массиве отчётов может оказаться undefined (устаревшее/битое состояние,
    // FRONT-2AD1). Раньше `.map(({ id })...)` ронял весь запрос статистики.
    const validReports = reports.filter((report): report is Report => Boolean(report));
    const question = validReports.map(({ id }: Report) => id);
    const commonParams = buildReportParams(globalFilters, allReportList, question, page, responsesLimit, period);
    const withFiles = allReportList.some(({ type }) => type === QuestionType.FILE_UPLOAD);
    // Один ssid на всю пачку мини-запросов просмотра отчёта: бэкенд переиспользует
    // по нему список подходящих респондентов вместо пересчёта цепочки фильтров.
    const ssid = getReportSsid(surveyId, commonParams);

    const [{ stat }, { list }, { count, size }] = await Promise.all([
        POST(
            'get/stat_question',
            undefDestroy(
                Object.assign(
                    {
                        group,
                        ssid,
                    },
                    commonParams as any,
                ),
            ),
        ),
        POST(
            'get/list_question',
            undefDestroy(
                Object.assign(
                    {
                        omit: field ? listOmitFilter.filter((i) => i !== field).join(',') : undefined,
                        ssid,
                    },
                    commonParams as any,
                ),
            ),
        ),
        withFiles
            ? POST(
                  'get/files_stat',
                  Object.assign(
                      {
                          survey: surveyId,
                      },
                      commonParams as any,
                  ),
              )
            : { count: 0, size: 0 },
    ]);

    const statWithReps = stat.map((s: any) => ({
        resp: list.find((listItem: any) => s.id === listItem.id),
        stat: s,
    }));

    return {
        reports: validReports.map((r: Report) => mapReportStat(r, statWithReps)),
        filesSize: size,
        filesCount: count,
    };
};
